import { useState, useEffect, useCallback } from "react";
import "./App.css";

// Layout components
import { Header, Footer, MobileStickyBar } from "./components/layout";

// Section components
import {
  HeroSection,
  KinekSzolSection,
  ProblemaSection,
  MegoldasSection,
  MitKapszSection,
  FolyamatSection,
  CsomagokSection,
  MidAnchorSection,
  KarbantartasSection,
  LeadMagnetSection,
  GyikSection,
  FinalCtaSection
} from "./components/sections";

// Modal components
import {
  LeadFormModal,
  PackageDetailsModal,
  ProPackageDetailsModal,
  PrivacyModal,
  TermsModal,
  CookieConsentModal
} from "./components/modals";

// Utils
import { trackEvent, trackSectionView, trackFormStart, trackButtonClick } from "./utils/analytics";
import initScrollAnimations from "./utils/scrollAnimations";

// FONTOS: Ebben a projektben TILOS bármilyen "Made with Emergent" branding, badge vagy külső emergent script!

function App() {
  // Guardian: Emergent badge eltávolítása (egyszer fut le)
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.remove();
  }, []);

  // Initialize scroll animations
  useEffect(() => {
    initScrollAnimations();
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [sketchModalOpen, setSketchModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [proDetailsModalOpen, setProDetailsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  // Scroll depth tracking
  useEffect(() => {
    const scrollDepths = [25, 50, 75, 90];
    const trackedDepths = new Set();

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      scrollDepths.forEach(depth => {
        if (scrollPercent >= depth && !trackedDepths.has(depth)) {
          trackedDepths.add(depth);
          trackEvent('scroll_depth', {
            percent_scrolled: depth,
            page_location: window.location.href
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track section visibility with Intersection Observer
  useEffect(() => {
    const sections = document.querySelectorAll('section[id]');
    const trackedSections = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !trackedSections.has(entry.target.id)) {
          trackedSections.add(entry.target.id);
          trackSectionView(entry.target.id);
        }
      });
    }, { threshold: 0.3 });

    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  // Track modal opens
  const handleOpenModal = useCallback(() => {
    setModalOpen(true);
    trackFormStart('quote');
    trackButtonClick('Ajánlatkérés', 'modal_trigger');
  }, []);

  const handleOpenSketch = useCallback(() => {
    setSketchModalOpen(true);
    trackFormStart('sketch');
    trackButtonClick('Vázlatkérés', 'modal_trigger');
  }, []);

  return (
    <div className="App min-h-screen bg-ink">
      <Header onOpenModal={handleOpenModal} />
      
      <main>
        <HeroSection
          onOpenModal={handleOpenModal}
          onOpenSketch={handleOpenSketch}
        />
        <KinekSzolSection />
        <ProblemaSection />
        <MegoldasSection />
        <MitKapszSection />
        <FolyamatSection />
        <CsomagokSection
          onOpenModal={handleOpenModal}
          onOpenDetails={() => setDetailsModalOpen(true)}
          onOpenProDetails={() => setProDetailsModalOpen(true)}
        />
        <MidAnchorSection />
        <KarbantartasSection />
        <LeadMagnetSection onOpenSketch={handleOpenSketch} />
        <GyikSection />
        <FinalCtaSection
          onOpenModal={handleOpenModal}
          onOpenSketch={handleOpenSketch}
        />
      </main>

      <Footer 
        onOpenPrivacy={() => setPrivacyModalOpen(true)}
        onOpenTerms={() => setTermsModalOpen(true)}
      />

      <MobileStickyBar
        onOpenModal={handleOpenModal}
        onOpenSketch={handleOpenSketch}
      />

      {/* Modals */}
      <LeadFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        type="quote"
      />
      <LeadFormModal
        open={sketchModalOpen}
        onClose={() => setSketchModalOpen(false)}
        type="sketch"
      />
      <PackageDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
      />
      <ProPackageDetailsModal
        open={proDetailsModalOpen}
        onClose={() => setProDetailsModalOpen(false)}
      />
      <PrivacyModal
        open={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
      />
      <TermsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
      <CookieConsentModal />
    </div>
  );
}

export default App;
